import { useUser } from "@clerk/clerk-expo";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { Masks } from "react-native-mask-input";

import { Grid, Input, MaskedInput, Select, getGridLayout } from "@/components";
import {
  GENDER_IDENTITY_OPTIONS,
  PRONOUN_OPTIONS,
  SEX_ASSIGNED_AT_BIRTH_OPTIONS,
  TIMEZONE_OPTIONS,
} from "@/settings/constants";
import type { UserProfileSchemaType } from "../tabs/profile";

export interface PersonalInfoProps {
  control: Control<UserProfileSchemaType>;
  errors: FieldErrors<UserProfileSchemaType>;
}

export function PersonalInfo({ control, errors }: PersonalInfoProps) {
  const { user } = useUser();

  return (
    <Grid>
      <Input
        label="Phone number"
        disabled
        value={user?.primaryPhoneNumber?.phoneNumber || ""}
        readonly
        rootProps={{
          ...getGridLayout(6),
        }}
      />
      <Controller
        control={control}
        name="email"
        render={({
          field: { onChange, onBlur, ref, value },
          fieldState: { error },
        }) => (
          <Input.Controlled
            ref={ref}
            label="Email address"
            value={value ?? undefined}
            placeholder="Email address"
            errorText={error?.message}
            onChangeText={onChange}
            onBlur={onBlur}
            rootProps={{
              ...getGridLayout(6),
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="preferredName"
        render={({ field: { onChange, onBlur, ref, value } }) => (
          <Input
            ref={ref}
            label="Preferred name"
            placeholder="Preferred name"
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            rootProps={{
              ...getGridLayout(6),
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="firstName"
        render={({ field: { onChange, onBlur, ref, value } }) => (
          <Input
            ref={ref}
            label="Legal first name"
            placeholder="Legal first name"
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            rootProps={{
              ...getGridLayout(6),
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="lastName"
        render={({ field: { onChange, onBlur, ref, value } }) => (
          <Input
            ref={ref}
            label="Legal last name"
            placeholder="Legal last name"
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            rootProps={{
              ...getGridLayout(6),
            }}
          />
        )}
      />

      <Controller
        control={control}
        name="dateOfBirth"
        render={({ field: { onChange, onBlur, ref, value } }) => (
          <MaskedInput.Controlled
            ref={ref}
            label="Date of birth"
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            inputMode="numeric"
            mask={Masks.DATE_MMDDYYYY}
            placeholder="mm/dd/yyyy"
            errorText={errors.dateOfBirth?.message}
            rootProps={{
              ...getGridLayout(6),
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="sexAssignedAtBirth"
        render={({ field: { onChange, ref, value } }) => (
          <Select
            ref={ref}
            label="Sex assigned at birth"
            placeholder="Select option"
            items={SEX_ASSIGNED_AT_BIRTH_OPTIONS}
            value={value}
            onValueChange={onChange}
            {...getGridLayout(6)}
          />
        )}
      />
      <Controller
        control={control}
        name="genderIdentity"
        render={({ field: { onChange, ref, value } }) => (
          <Select
            ref={ref}
            label="Gender identity"
            placeholder="Select option"
            items={GENDER_IDENTITY_OPTIONS}
            value={value}
            onValueChange={onChange}
            {...getGridLayout(6)}
          />
        )}
      />
      <Controller
        control={control}
        name="pronouns"
        render={({ field: { onChange, ref, value } }) => (
          <Select
            ref={ref}
            label="Pronouns"
            placeholder="Select option"
            items={PRONOUN_OPTIONS}
            value={value}
            onValueChange={onChange}
            {...getGridLayout(6)}
          />
        )}
      />
      <Controller
        control={control}
        name="timezone"
        render={({ field: { onChange, ref, value } }) => (
          <Select
            ref={ref}
            label="Timezone"
            items={TIMEZONE_OPTIONS}
            value={value}
            onValueChange={onChange}
            {...getGridLayout(6)}
          />
        )}
      />
    </Grid>
  );
}
